import { styled } from "styled-components";

import { colorCSS, fontSizes, h5 } from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

const borderStyle = `1px solid ${colorCSS("N40")}`;
const highlightedBorderStyle = `2px solid ${colorCSS("T300")}`;

export const Grid = styled.div<{ planCount: number }>`
    ${redundantCssFromKebab("display", "grid")}
    ${redundantCssFromKebab("grid-template-columns", "1fr")}
    ${redundantCssFromKebab("margin", "0 auto")}
    ${redundantCssFromKebab("max-width", "100%")}
    ${redundantCssFromKebab("padding", "0 1rem")}
    ${redundantCssFromKebab("gap", "1rem")}
    
    @media (min-width: 576px) {
        max-width: 540px;
    }

    @media (min-width: 768px) {
        max-width: 720px;
    }

    @media (min-width: 992px) {
        max-width: 960px;
        grid-template-columns: repeat(
            ${(props) => props.planCount},
            minmax(0, 1fr)
        );
        ${redundantCssFromKebab("gap", "0")}
    }

    @media (min-width: 1200px) {
        max-width: 1140px;
    }

    @media (min-width: 1400px) {
        max-width: 1320px;
    }
`;

export const PlanContainer = styled.div`
    ${redundantCssFromKebab("display", "contents")}

    @media (max-width: 991px) {
        ${redundantCssFromKebab("display", "block")}
    }
`;

export const Item = styled.div<{
    bottom?: boolean;
    first?: boolean;
    highlight?: boolean;
    lgOrder: number;
    show?: boolean;
    top?: boolean;
}>`
    background-color: ${colorCSS("N0")};
    padding: 1.5rem 1rem 0;
    text-align: left;

    border-left: ${(props) =>
        props.highlight ? highlightedBorderStyle : borderStyle};
    border-right: ${(props) =>
        props.highlight ? highlightedBorderStyle : borderStyle};

    ${(props) =>
        props.top &&
        `border-top: ${
            props.highlight ? highlightedBorderStyle : borderStyle
        };`}

    ${(props) =>
        props.bottom &&
        `
        padding-bottom: 2rem;
        border-bottom: ${
            props.highlight ? highlightedBorderStyle : borderStyle
        };
        padding-top: 1rem;
        a, button {
            display: block;
            width: 100%;
        }
    `}
    
    ${(props) => !props.show && "opacity: 0;"}
    
    @media (min-width: 992px) {
        margin-bottom: 0;
        ${(props) => !props.first && !props.highlight && "border-left: 0;"}
    }

    order: ${(props) => props.lgOrder + 1};
`;

export const Title = styled.h2`
    ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
    ${redundantCssFromKebab("text-transform", "uppercase")}
    ${redundantCssFromKebab("margin", "0")}
`;

export const Description = styled.p`
    ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
    ${redundantCssFromKebab("margin-bottom", "0")}
`;

export const Price = styled.div`
    ${redundantCssFromKebab("font-size", "3rem")}
    ${redundantCssFromKebab("line-height", "1")}
    ${redundantCssFromKebab("margin-bottom", "0.5rem")}
`;

export const PriceAppend = styled.span`
    ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
`;

export const PriceDetail = styled.p`
    ${redundantCssFromKebab("color", `${colorCSS("N400")}`)}
    ${redundantCssFromKebab("font-size", `${fontSizes.xxs}`)}
    ${redundantCssFromKebab("margin", "0")}
`;

export const PriceMax = styled.div`
    ${redundantCssFromKebab("display", "flex")}
    ${redundantCssFromKebab("gap", "0.5rem")}
    ${redundantCssFromKebab("margin-top", "1rem")}

    svg {
        ${redundantCssFromKebab("margin-top", "0.25rem")}
        ${redundantCssFromKebab("height", "1.5rem")}
        ${redundantCssFromKebab("width", "1.5rem")}
    }

    p {
        ${redundantCssFromKebab("margin-bottom", "0")}
    }
`;

export const FeatureList = styled.div`
    ${redundantCssFromKebab("display", "flex")}
    ${redundantCssFromKebab("flex-direction", "column")}
    ${redundantCssFromKebab("text-align", "left")}

    h3 {
        ${h5}
    }

    button {
        ${redundantCssFromKebab("background", "transparent")}
        ${redundantCssFromKebab("border", "0")}
        ${redundantCssFromKebab("position", "relative")}
        ${redundantCssFromKebab("padding", "0 1rem 1rem")}
        ${redundantCssFromKebab("outline", "none !important")}
        ${redundantCssFromKebab("text-align", "left")}

        &::before {
            ${redundantCssFromKebab("content", '"+"')}
            ${redundantCssFromKebab("position", "absolute")}
            ${redundantCssFromKebab("left", "0")}
        }
    }

    &[data-open="true"] {
        button::before {
            ${redundantCssFromKebab("content", '"-"')}
        }
    }

    @media (min-width: 992px) {
        button {
            ${redundantCssFromKebab("display", "none")}
        }

        > div {
            ${redundantCssFromKebab("border-top", `${borderStyle}`)}
            ${redundantCssFromKebab("padding-top", "1.25rem")}
        }
    }

    ul {
        ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
        ${redundantCssFromKebab("list-style-type", "none")}
        ${redundantCssFromKebab("margin", "0")}
        ${redundantCssFromKebab("padding", "0 0 0 1.5rem")}

        li {
            ${redundantCssFromKebab("margin", "0 0 0.5rem")}
            ${redundantCssFromKebab("position", "relative")}

            code {
                ${redundantCssFromKebab(
                    "background",
                    `${colorCSS("trelloblue")}`
                )}
                ${redundantCssFromKebab("border-radius", "0.25rem")}
                ${redundantCssFromKebab("color", `${colorCSS("N0")}`)}
                ${redundantCssFromKebab("display", "inline-block")}
                ${redundantCssFromKebab("font-family", "inherit")}
                ${redundantCssFromKebab("font-size", `${fontSizes.xxs}`)}
                ${redundantCssFromKebab("padding", "0.12rem 0.5rem 0.25rem")}
                ${redundantCssFromKebab("vertical-align", "baseline")}
                ${redundantCssFromKebab("white-space", "nowrap")}
            }

            &:last-child {
                ${redundantCssFromKebab("margin-bottom", "0")}
            }

            &::before {
                ${redundantCssFromKebab(
                    "background-image",
                    "url(\"data:image/svg+xml,%3Csvg width='14' height='10' viewBox='0 0 14 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.73534 4.3223C1.36105 3.91618 0.728414 3.89038 0.3223 4.26467C-0.0838134 4.63895 -0.109615 5.27159 0.264671 5.6777L3.87678 9.59697C4.41431 10.1231 5.21446 10.1231 5.71114 9.62638C5.7724 9.56623 5.7724 9.56623 6.07539 9.26827C6.36994 8.97855 6.69814 8.65556 7.05157 8.3075C8.06144 7.31303 9.07132 6.31689 10.014 5.38477L10.0543 5.34489C11.7291 3.68869 13.0004 2.42365 13.712 1.70223C14.0998 1.30904 14.0954 0.675886 13.7022 0.288053C13.309 -0.0997796 12.6759 -0.0954305 12.2881 0.297767C11.5843 1.01131 10.3169 2.27244 8.648 3.92281L8.60773 3.96263C7.66616 4.89368 6.65722 5.88889 5.64826 6.88247C5.35784 7.16847 5.08449 7.43752 4.83288 7.68506L1.73534 4.3223Z' fill='%2344C476'/%3E%3C/svg%3E%0A\")"
                )}
                ${redundantCssFromKebab("background-repeat", "no-repeat")}
                ${redundantCssFromKebab("background-position", "center center")}
                ${redundantCssFromKebab("background-size", "contain")}
                ${redundantCssFromKebab("content", '""')}
                ${redundantCssFromKebab("height", "1rem")}
                ${redundantCssFromKebab("left", "-1.5rem")}
                ${redundantCssFromKebab("margin", "0 auto")}
                ${redundantCssFromKebab("overflow", "hidden")}
                ${redundantCssFromKebab("position", "absolute")}
                ${redundantCssFromKebab("text-indent", "-9999px")}
                ${redundantCssFromKebab("top", "0.25rem")}
                ${redundantCssFromKebab("vertical-align", "middle")}
                ${redundantCssFromKebab("width", "1rem")}
            }
        }
    }
`;

const trackHeight = 8;
const thumbSize = 24;

export const UserInputWrapper = styled.div`
    ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
    ${redundantCssFromKebab("margin-top", "1rem")}

    input {
        &[type="number"] {
            ${redundantCssFromKebab("appearance", "textfield")}
            ${redundantCssFromKebab("border-color", `${colorCSS("N900")}`)}
            ${redundantCssFromKebab("border-radius", "0")}
            ${redundantCssFromKebab("border-style", "solid")}
            ${redundantCssFromKebab("border-width", "0 0 1px")}
            ${redundantCssFromKebab("color", `${colorCSS("N900")}`)}
            ${redundantCssFromKebab("font-size", `${fontSizes.lg}`)}
            ${redundantCssFromKebab("font-weight", "500")}
            ${redundantCssFromKebab("margin", "0 0.25rem")}
            ${redundantCssFromKebab("padding", "0")}
            ${redundantCssFromKebab("text-align", "center")}
            ${redundantCssFromKebab("width", "4.5rem")}

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                ${redundantCssFromKebab("appearance", "none")}
                ${redundantCssFromKebab("margin", "0")}
            }
        }

        &[type="range"] {
            ${redundantCssFromKebab("appearance", "none")}
            ${redundantCssFromKebab("margin", "1.75rem 0 0.75rem")}
            ${redundantCssFromKebab("width", "100%")}

            &::-webkit-slider-runnable-track {
                ${redundantCssFromKebab(
                    "background-color",
                    `${colorCSS("N40")}`
                )}
                ${redundantCssFromKebab(
                    "border-radius",
                    `${trackHeight / 2}px`
                )}
                ${redundantCssFromKebab("border", "0")}
                ${redundantCssFromKebab("cursor", "pointer")}
                ${redundantCssFromKebab("height", `${trackHeight}px`)}
                ${redundantCssFromKebab("width", "100%")}
            }

            &::-webkit-slider-thumb {
                ${redundantCssFromKebab("appearance", "none")}
                ${redundantCssFromKebab(
                    "background-color",
                    `${colorCSS("N0")}`
                )}
                ${redundantCssFromKebab("border-radius", "50%")}
                ${redundantCssFromKebab(
                    "border",
                    `1px solid ${colorCSS("N30")}`
                )}
                ${redundantCssFromKebab(
                    "box-shadow",
                    `0px 2px 4px ${colorCSS("N900", 0.25)}`
                )}
                ${redundantCssFromKebab("cursor", "pointer")}
                ${redundantCssFromKebab("height", `${thumbSize}px`)}
                ${redundantCssFromKebab(
                    "margin-top",
                    `${trackHeight / 2 - thumbSize / 2}px`
                )}
                ${redundantCssFromKebab("width", `${thumbSize}px`)}
            }

            &::-moz-range-track {
                ${redundantCssFromKebab(
                    "background-color",
                    `${colorCSS("N40")}`
                )}
                ${redundantCssFromKebab(
                    "border-radius",
                    `${trackHeight / 2}px`
                )}
                ${redundantCssFromKebab("border", "0")}
                ${redundantCssFromKebab("cursor", "pointer")}
                ${redundantCssFromKebab("height", `${trackHeight}px`)}
                ${redundantCssFromKebab("width", "100%")}
            }

            &::-moz-range-thumb {
                ${redundantCssFromKebab("appearance", "none")}
                ${redundantCssFromKebab(
                    "background-color",
                    `${colorCSS("N0")}`
                )}
                ${redundantCssFromKebab("border-radius", "50%")}
                ${redundantCssFromKebab(
                    "border",
                    `1px solid ${colorCSS("N30")}`
                )}
                ${redundantCssFromKebab(
                    "box-shadow",
                    `0px 2px 4px ${colorCSS("N900", 0.25)}`
                )}
                ${redundantCssFromKebab("cursor", "pointer")}
                ${redundantCssFromKebab("height", `${thumbSize}px`)}
                ${redundantCssFromKebab(
                    "margin-top",
                    `${trackHeight / 2 - thumbSize / 2}px`
                )}
                ${redundantCssFromKebab("width", `${thumbSize}px`)}
            }
        }

        &:focus {
            ${redundantCssFromKebab("outline", "none")}

            &::-webkit-slider-thumb {
                ${redundantCssFromKebab(
                    "background-color",
                    `${colorCSS("N800")}`
                )}
            }

            &::-moz-range-thumb {
                ${redundantCssFromKebab(
                    "background-color",
                    `${colorCSS("N800")}`
                )}
            }

            &:not(:focus-visible) {
                &::-webkit-slider-thumb {
                    ${redundantCssFromKebab(
                        "background-color",
                        `${colorCSS("N0")}`
                    )}
                }

                &::-moz-range-thumb {
                    ${redundantCssFromKebab(
                        "background-color",
                        `${colorCSS("N0")}`
                    )}
                }
            }
        }
    }
`;
